/* eslint-disable react-hooks/refs --
 * AuthProvider намеренно императивен: токены живут в refs (не в state), а
 * configureClient() вызывается СИНХРОННО во время рендера — чтобы дети успели
 * подхватить геттеры до первого запроса. Перенос в useEffect (что удовлетворило бы
 * правило) запрещён: дочерние компоненты начнут запросы до конфигурации клиента.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { configureClient, revokeSession } from '@/api/client'
import type { TokenPairResponse } from '@/api/types'
import {
  clearIsAdmin,
  clearRefresh,
  loadIsAdmin,
  loadRefresh,
  REFRESH_KEY,
  saveIsAdmin,
  saveRefresh,
} from './storage'

export interface AuthState {
  isAuthenticated: boolean
  isAdmin: boolean
  login: (pair: TokenPairResponse) => void
  logout: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthState | null>(null)

// is_admin/sub декодируются из JWT на клиенте (atob) без проверки подписи.
function extractIsAdmin(accessToken: string): boolean {
  try {
    const b64 = accessToken.split('.')[1]
    const json = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json) as Record<string, unknown>
    return payload.is_admin === true
  } catch {
    return false
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Refs всегда содержат актуальные значения — никаких stale closures в колбэках,
  // передаваемых в configureClient(). Access живёт только в памяти.
  const initialRefresh = loadRefresh()
  const accessRef = useRef<string | null>(null)
  const refreshRef = useRef<string | null>(initialRefresh)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(initialRefresh !== null)
  // Инициализируем из кеша — без мигания меню при перезагрузке.
  const [isAdmin, setIsAdmin] = useState<boolean>(loadIsAdmin)

  // configureClient вызывается СИНХРОННО во время рендера (не в useEffect), чтобы
  // дочерние компоненты подхватили актуальные геттеры до первого запроса.
  const installedRef = useRef(false)
  if (!installedRef.current) {
    installedRef.current = true
    configureClient({
      getAccessToken: () => accessRef.current,
      // Читаем из localStorage: соседняя вкладка могла ротировать токен, и наш ref устарел.
      getRefreshToken: () => loadRefresh() ?? refreshRef.current,
      onTokenRefreshed: (pair) => {
        accessRef.current = pair.accessToken
        refreshRef.current = pair.refreshToken
        saveRefresh(pair.refreshToken)
        const admin = extractIsAdmin(pair.accessToken)
        saveIsAdmin(admin)
        setIsAdmin(admin)
      },
      onSessionExpired: () => {
        accessRef.current = null
        refreshRef.current = null
        clearRefresh()
        clearIsAdmin()
        setIsAuthenticated(false)
        setIsAdmin(false)
      },
    })
  }

  const login = useCallback((pair: TokenPairResponse) => {
    accessRef.current = pair.accessToken
    refreshRef.current = pair.refreshToken
    saveRefresh(pair.refreshToken)
    const admin = extractIsAdmin(pair.accessToken)
    saveIsAdmin(admin)
    setIsAdmin(admin)
    setIsAuthenticated(true)
  }, [])

  // Отзываем цепочку на сервере, но состояние чистим сразу: ждать сеть при выходе незачем.
  const logout = useCallback(() => {
    void revokeSession()
    accessRef.current = null
    refreshRef.current = null
    clearRefresh()
    clearIsAdmin()
    setIsAuthenticated(false)
    setIsAdmin(false)
  }, [])

  // Синхронизация вкладок: refresh одноразовый, поэтому ротацию или выход в соседней
  // вкладке нужно подхватывать, иначе эта отправит уже потраченный токен.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== REFRESH_KEY) return
      if (event.newValue === null) {
        accessRef.current = null
        refreshRef.current = null
        clearIsAdmin()
        setIsAuthenticated(false)
        setIsAdmin(false)
        return
      }
      refreshRef.current = event.newValue
    }
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({ isAuthenticated, isAdmin, login, logout }),
    [isAuthenticated, isAdmin, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
